import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetStatistics } from "@/services/Orders";
import { TotalSalesPerSalesItem } from "@/types";
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import Table from "@/components/atoms/Table";
import Loader from "@/components/atoms/Loader";
import SortableHeader from "../../molecules/SortableHeader";
import NumberFormatter from "@/components/atoms/NumberFormatter";

import styles from "./styles.module.css";

export default function Statistics() {
  const { t } = useTranslation();

  const { data: statisticsData, isLoading } = useGetStatistics();

  const columns: ColumnDef<TotalSalesPerSalesItem>[] = useMemo(
    () => [
      {
        header: ({ column }) => <SortableHeader column={column} label={t("statistics:type")} />,
        accessorKey: "salesItem.salesItem",
        cell: ({ row }) => <div>{t("enumerables:plastic." + row.original.salesItem.salesItem)}</div>,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("statistics:sumSalesItem")} />,
        accessorKey: "totalSumInNOK",
        cell: ({ row }) => <NumberFormatter value={row.original.totalSumInNOK} />,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("statistics:amountSalesItem")} />,
        accessorKey: "totalAmountInKg",
        cell: ({ row }) => <NumberFormatter value={row.original.totalAmountInKg} />,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("statistics:feeSalesItem")} />,
        accessorKey: "totalFee",
        cell: ({ row }) => <NumberFormatter value={row.original.totalFee} />,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("statistics:ordersSalesItem")} />,
        accessorKey: "totalNumberOfOrders",
        cell: ({ row }) => <NumberFormatter value={row.original.totalNumberOfOrders} />,
      },
    ],
    []
  );

  const table = useReactTable({
    data: statisticsData ? statisticsData.totalSalesPerSalesItem : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="admin-statistics">
      <title>{`Admin - Reviver - ${t("statistics:statistics")}`}</title>
      <meta name="description" content={`Admin - Reviver - ${t("statistics:statistics")}`} />
      <h3 className="mbottom20">{t("statistics:statistics")}</h3>
      {!!statisticsData && (
        <>
          <div className={styles.statistics}>
            <div className={styles.statistic}>
              <div className={styles.label}>{t("statistics:noActiveOrders")}</div>
              <div className={styles.value}>
                <NumberFormatter value={statisticsData.activeOrders} />
              </div>
            </div>
            <div className={styles.statistic}>
              <div className={styles.label}>{t("statistics:noTotalOrders")}</div>
              <div className={styles.value}>
                <NumberFormatter value={statisticsData.orders} />
              </div>
            </div>
            <div className={styles.statistic}>
              <div className={styles.label}>{t("statistics:noSales")}</div>
              <div className={styles.value}>
                <NumberFormatter value={statisticsData.transactions} />
              </div>
            </div>
            <div className={styles.statistic}>
              <div className={styles.label}>{t("statistics:sumFee")}</div>
              <div className={styles.value}>
                <NumberFormatter value={statisticsData.fees} />
              </div>
            </div>
            <div className={styles.statistic}>
              <div className={styles.label}>{t("statistics:sumSales")}</div>
              <div className={styles.value}>
                <NumberFormatter value={statisticsData.totalSumInNok} />
              </div>
            </div>
          </div>

          <Table>
            <Table.Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Th key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Table.Th>
                  ))}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {table.getRowModel().rows.map((row) => (
                <Table.Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </div>
  );
}
